import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { Observable, Subscription, forkJoin, catchError, of, Subject } from 'rxjs';
import { filter, tap, switchMap, map, finalize, takeUntil } from 'rxjs/operators';
import { groupBy } from 'lodash';
import { MessageService } from 'primeng/api';
import moment from 'moment';

import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { ViDimensionCard, DimQuestion, QuestionScore } from '../../models/vi-dimension-card.model';
import { ViQuestionApi } from '@api/visibilityIntelligence/models/vi-question.api.model';
import { AnswerEngineOption, getAllEngineOptions } from '../../models/answer-engine-option.model';
import { ViMonitor } from '../../models/vi-monitor.model';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { AnswerEngineTypesEnum } from '../../enums/answer-engine-types.enum';
import { ViSourceOutlet } from '../../models/vi-source-outlet.model';
import { ViBrandIdentity } from '../../models/vi-brand-identity.model';
import { ViInsightPrompt } from '../../models/vi-insight-prompt.model';
import { ViAnalysisEnum } from '../../enums/vi-analysis.enum';

import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { ViChartDrillDownService } from '../../services/vi-chart-drill-down.service';
import {
    VisibilityIntelligenceSummaryChartTypesEnum as ViChartTypeEnum,
    viSummaryCardColorMap
} from '../../enums/vi-summary-chart-types.enum';
import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '@Common/Charts/components/CardChart/card-chart.component';
import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { DateHelper, DayKey } from '@Common/Forms/helpers/date.helper';
import { ViApiService } from '@api/visibilityIntelligence/vi.api.service';
import { ViRunRequestApi } from '@api/visibilityIntelligence/models/vi-run-request.api.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { ViReportsSearchService } from '../../services/vi-reports-search.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { MentionModel } from '@api/mentions/model/mention.model';
import { viApiToEngineType } from '../../helpers/vi-engine-type.map';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { ViAnswerEngineSource } from '../../models/vi-answer-engine-source.model';
import { ViAnswerEngineApiEnum } from '@api/visibilityIntelligence/enums/vi-answer-engine.api.enum';
import { OutletsPublicApi } from '@api/targeting/outlets-public.api';
import { SearchContactsAndOutletsTypeApiEnum } from '@api/targeting/enum/search-contacts-and-outlets-type.enum';
import { JournodexSearchesApi } from '@api/targeting/journodex-searches.api';
import { MentionsApi } from '@api/mentions/mentions.api';
import { isBlockedHost } from '../../helpers/vi-outlet-url-block-list';

// we have to convert all the run times to local time first, because the date picker uses Date().. which automatically converts your time to
// local time.. so when you click on a date.. it will be (ex: oct 22, 00:00:00 local time)
// so the dates can be a bit off depending
const toRunDay = (iso: string): DayKey => {
    // Bucket by UTC day: dateChanged() treats the picked calendar day as a UTC day (see toUtcDate),
    // so converting to local here would shift runs to the next/previous day in non-UTC timezones.
    return moment.utc(iso).format('YYYY-MM-DD');
};


enum ViewMode {
    LIST = 'LIST',
    GROUP = 'GROUP'
}

export enum VisibilityMode {
    SHOW = 'SHOW',
    HIDE = 'HIDE'
}

@Component({
    selector: 'agpr-vi-chart-drill-down-dialog',
    templateUrl: './vi-chart-drill-down.dialog.component.html',
    styleUrls: ['./vi-chart-drill-down.dialog.component.scss'],
    providers: [
        ViChartDrillDownService,
        ViReportsSearchService,
        {
            provide: 'SortOrderServiceInterface',
            useExisting: ViChartDrillDownService
        }
    ],
    standalone: false
})
export class ViChartDrillDownDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() chartType: ViChartTypeEnum;
    @Input() cardSeries: ReportSeriesModel[] = [];
    @Input() dateQuery: DateQueryModel;
    @Input() chartLabel: string;

    readonly visibilityOptions: DropdownOptionModel<VisibilityMode>[] = [
        new DropdownOptionModel('Vi.visibilityOption.show', VisibilityMode.SHOW),
        new DropdownOptionModel('Vi.visibilityOption.hide', VisibilityMode.HIDE)
    ];

    selectedDate: Date;
    dayOptions: DayKey[] = [];
    previousDate: Date;
    mostRecentDate: Date;

    filterDimensionCards: ViDimensionCard<DimQuestion>[] = Object.values(ViDimensionEnum).map(x =>
        ({
            dimension: x as ViDimensionEnum,
            questions: [],
            isExpanded: true
        })
    );
    filterResults: DimQuestion[] = [];

    selectedQuestion: DimQuestion;

    answerEngineOptions: AnswerEngineOption[] = [];
    previousAnswerEngineOptions: AnswerEngineOption[] = [];
    selectedEngineType: AnswerEngineTypesEnum;
    startEndTimeDifference: moment.Duration = null;

    outletList: ViSourceOutlet[] = [];
    outletListLoading: boolean = false;

    brand: ViBrandIdentity;
    selectedInsightsLanguage: string = '';
    executedPrompt: string = '';

    cardColor: CardChartComponentHighlightColorEnum;
    numberOfDecimals: number;

    viewMode: ViewMode = ViewMode.GROUP;
    selectedVisibilityOption: DropdownOptionModel<VisibilityMode> = this.visibilityOptions[0];

    defaultClassHeight = 'agpr-drilldown-chartHeight-smallest';


    private currentMonitor: ViMonitor;

    readonly id = 'chartDrillDownDialog';
    readonly viewModeType = ViewMode;
    readonly buttonType = ButtonTypesEnum;
    readonly subscriptions: Subscription[] = [];
    readonly viChartTypeEnums = ViChartTypeEnum;
    readonly cardChartLayoutType = CardChartComponentLayoutEnum;
    readonly percentageCharts: ViChartTypeEnum[] = [
        ViChartTypeEnum.PRESENCE_RATE,
        ViChartTypeEnum.RISK_RATE,
    ];
    readonly prompts: ViInsightPrompt[] = [
        {
            analysisType: ViAnalysisEnum.SENTIMENT_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.REPRESENTATION_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.COMPETITIVE_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CITATION_AUTHORITY_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CUSTOM,
            isCustomPrompt: true,
            expanded: false,
            aiResponse: ''
        }
    ];

    readonly engineLabelMap: Record<ViAnswerEngineApiEnum, string> = {
        [ViAnswerEngineApiEnum.openai]: 'OpenAI',
        [ViAnswerEngineApiEnum.gemini]: 'Gemini',
        [ViAnswerEngineApiEnum.perplexity]: 'Perplexity',
        [ViAnswerEngineApiEnum.xai]: 'Grok',
        [ViAnswerEngineApiEnum.anthropic]: 'Claude',
    };

    readonly loading$ = this.viChartDrillDownService.isLoading();
    private readonly searchDestroy$ = new Subject<void>();

    constructor(
        coreDialogService: CoreDialogService,
        private viMonitorService: ViMonitorService,
        private viChartDrillDownService: ViChartDrillDownService,
        private viReportsSearchService: ViReportsSearchService,
        private viApiService: ViApiService,
        private messageService: MessageService,
        private outletsPublicApi: OutletsPublicApi,
        private journodexSearchesApi: JournodexSearchesApi,
        private mentionsApi: MentionsApi,
    ) {
        super(coreDialogService, 'ViChartDrillDownDialog');
    }

    ngOnInit() {
        this.cardColor = viSummaryCardColorMap.get(this.chartType);
        this.numberOfDecimals = (this.chartType === ViChartTypeEnum.RANK_WHEN_LISTED) ? 2 : null;

        // skip 2 days back (since we bucket by day), to get proper run candence
        const previousPeriod = DateQueryModel.getPreviousPeriod(DateQueryModel.getPreviousPeriod(this.dateQuery));
        if (this.dateQuery.timezone) {
            previousPeriod.timezone = this.dateQuery.timezone;
        }

        const startTime = moment(this.dateQuery.absoluteRange.start);
        const endTime = moment(this.dateQuery.absoluteRange.end);
        this.startEndTimeDifference = moment.duration(endTime.diff(startTime));

        const useEnd = new Date().getTimezoneOffset() > 0;
        this.selectedDate = useEnd ? this.dateQuery.absoluteRange.end : this.dateQuery.absoluteRange.start;
        this.previousDate = previousPeriod.absoluteRange.start;

        this.drawTopChart();

        this.viChartDrillDownService.initialize({
            date: this.dateQuery,
            previousDate: previousPeriod,
            chartType: this.chartType
        });

        this.subscriptions.push(
            this.viMonitorService.currentResource$.pipe(
                filter(x => !!x),
                tap(monitor => {
                    this.currentMonitor = monitor;
                    this.brand = this.currentMonitor.brand;
                    this.answerEngineOptions = this.getEngineOptions(this.currentMonitor.answerEngines);
                    this.selectedEngineType = this.answerEngineOptions[0]?.type;
                }),
                switchMap(() => {
                    const request: ViRunRequestApi = {
                        start: this.currentMonitor.createdDate.getUTCMilliseconds(),
                    };
                    return this.viApiService.getRuns(this.currentMonitor.id, request);
                })
            ).subscribe(runs => {
                // we have to convert all the run times to local time first, because the date picker uses Date().. which automatically converts your time to
                // local time.. so when you click on a date.. it will be (ex: oct 22, 00:00:00 local time)
                // so the dates can be a bit off depending
                this.dayOptions = [...new Set(runs.map(x => toRunDay(x.publishedDate)))]
                    .sort()
                    .reverse();

                if (this.dayOptions.length > 0) {
                    this.mostRecentDate = DateHelper.fromDayKey(this.dayOptions[0]);
                } else {
                    this.mostRecentDate = null;
                }
            }),
            this.viChartDrillDownService.displayResults$.subscribe(
                results => {
                    this.filterResults = results;
                    this.filterDimensionCards = this.getDimensionCards(this.filterResults);

                    const questionExists = !!this.filterResults.find(x => x.id === this.selectedQuestion?.id);

                    if (questionExists) {
                        this.loadAllAnswerEngineResults();
                    } else {
                        let question: DimQuestion;

                        if (this.viewMode === ViewMode.GROUP) {
                            question = this.filterDimensionCards.find(card => card.questions.length > 0)?.questions[0];
                        } else if (this.viewMode === ViewMode.LIST) {
                            question = this.filterResults[0];
                        }
                        this.focusQuestion(question);
                    }
                }
            ),
            this.viChartDrillDownService.getError().subscribe(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Vi.chartDrillDown.error.heading',
                    summary: 'Vi.chartDrillDown.error.message',
                });
            })
        );
    }

    private drawTopChart() {
        if (this.cardSeries && this.dateQuery) {
            this.cardSeries.map(series => series.mentions.isPreviousPeriod ? series.mentions.skipPeriod = true : series.mentions.skipPeriod = false);
            this.cardSeries[0].fullSearch.isMonitorSearch = true;
            this.viReportsSearchService.setSearchCriteria(this.cardSeries[0].fullSearch);
            this.viReportsSearchService.setDateQuery(this.dateQuery, true);
            this.viReportsSearchService.deregisterSeries();
            this.viReportsSearchService.fulfillSeries(this.cardSeries);

            if (this.cardSeries?.length > 10) {
                this.defaultClassHeight = 'agpr-drilldown-chartHeight-largest';
            } else if (this.cardSeries?.length > 8) {
                this.defaultClassHeight = 'agpr-drilldown-chartHeight-large';
            } else if (this.cardSeries?.length > 5) {
                this.defaultClassHeight = 'agpr-drilldown-chartHeight-medium';
            } else if (this.cardSeries?.length > 3) {
                this.defaultClassHeight = 'agpr-drilldown-chartHeight-small';
            } else if (this.cardSeries?.length > 1) {
                this.defaultClassHeight = 'agpr-drilldown-chartHeight-smallest';
            }
        }
    }


    focusQuestion(question: ViQuestionApi & Partial<QuestionScore>) {
        if (!question) {

            return;
        }

        this.selectedQuestion = question as DimQuestion;
        this.loadAllAnswerEngineResults();
    }

    visibilityChanged() {
        this.viChartDrillDownService.setVisibility(this.selectedVisibilityOption.value);
    }

    dateChanged() {
        const dateInUTC = this.toUtcDate(this.selectedDate);
        this.dateQuery = new DateQueryModel();
        this.dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        this.dateQuery.timezone = 'UTC';

        this.dateQuery.absoluteRange = {
            start: dateInUTC,
            end: new Date(dateInUTC.getTime() + this.startEndTimeDifference.asMilliseconds()),
        };

        this.previousDate = this.getPreviousDay(this.selectedDate);
        const previousDateQuery =  DateQueryModel.getPreviousPeriod(DateQueryModel.getPreviousPeriod(this.dateQuery));
        if (this.dateQuery.timezone) {
            previousDateQuery.timezone = this.dateQuery.timezone;
        }
        this.viChartDrillDownService.setDate(this.dateQuery, previousDateQuery);
        this.drawTopChart();
    }


    /**
     * Returns a Date whose UTC wall-clock date/time matches the local wall-clock of `value`.
     * e.g. May 26 00:00 local (Toronto) -> 2026-05-26T00:00:00Z
     */
    private toUtcDate(value: Date | string): Date {
        const parsed = moment(value);

        // Build from components (not by adding minutes) so DST transitions can't shift the result.
        const date = new Date(Date.UTC(
            parsed.year(), parsed.month(), parsed.date(),
            parsed.hours(), parsed.minutes(), parsed.seconds(), parsed.milliseconds()
        ));
        return date;
    }

    changeViewMode() {
        if (this.viewMode === ViewMode.GROUP) {
            this.viewMode = ViewMode.LIST;
        } else if (this.viewMode === ViewMode.LIST) {
            this.viewMode = ViewMode.GROUP;
        }
    }

    close(overlayVisibility: boolean): void {
        if (!overlayVisibility) {
            this.closeDialog();
        }
    }

    isSelected(question: DimQuestion): boolean {
        const selected = this.selectedQuestion;

        if (!selected) {
            return false;
        }

        if (selected === question) {
            return true;
        }

        return selected.id !== undefined && selected.id !== null && selected.id === question.id;
    }


    // @TODO - move to common service as its shared in queries and answers

    private loadAllAnswerEngineResults() {
        this.searchDestroy$.next();
        this.outletListLoading = true;

        this.answerEngineOptions.forEach(option => {
            option.loading = true;
            option.answerText = '';
            option.sources = [];
        });

        this.prompts.forEach(prompt => {
            if (prompt.destroyApiCall$) {
                prompt.destroyApiCall$.next();
            }
            if (prompt.apiCall) {
                prompt.apiCall.unsubscribe();
            }
            prompt.aiResponse = '';
            prompt.generating = false;
            prompt.responseHtml = '';
            prompt.expanded = false;
        });

        const criteria = new MentionSearchCriteriaModel(undefined, true);
        criteria.isMonitorSearch = true;
        // @HACK weird b/e LLM_RESPONSE is a MediaTypeEnum
        criteria.query.includedMediaTypes = [new MediaTypeModel('LLM_RESPONSE' as MediaTypeEnum)];
        criteria.query.aiAgent.include = [this.currentMonitor.agentId];

        if (this.selectedQuestion?.id) {
            criteria.query.aiAgentSource.include = [+this.selectedQuestion.id];
        }

        if (this.selectedDate) {
            const dateQuery = new DateQueryModel();
            dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
            dateQuery.timezone = 'UTC';

            const wholeEndDay = new Date(this.selectedDate);
            wholeEndDay.setHours(23, 59, 59, 999);
            if (this.previousDate) {
                dateQuery.absoluteRange = {
                    start: this.toUtcDate(this.previousDate),
                    end: this.toUtcDate(wholeEndDay)
                };
            } else {
                const wholeStartDay = new Date(this.selectedDate);
                wholeStartDay.setHours(0, 0, 0, 0);
                dateQuery.absoluteRange = {
                    start: this.toUtcDate(wholeStartDay),
                    end: this.toUtcDate(wholeEndDay)
                };
            }

            this.searchForAnsers(criteria, dateQuery);
        }
    }


    /**
     * Mentions the source produced over the sample window. Returns null on failure so the banner falls
     * back to "N/A" rather than showing a number we cannot stand behind.
     */
    private searchForAnsers(criteria: MentionSearchCriteriaModel, dateQuery: DateQueryModel) {
        if (!dateQuery || !dateQuery) {
            return;
        }

        criteria.query.dateQuery = dateQuery;
        return this.mentionsApi.viSearchAccount(criteria.getApiEntity()).pipe(
            takeUntil(this.searchDestroy$),
            switchMap((response: MentionsSearchResponseModel) => {
                const filteredMentions: MentionModel[] = response.mentions.filter(mention => {
                    return moment.utc(mention.publishedDate as number).format('YYYY-MM-DD') === DateHelper.toDayKey(this.selectedDate);
                });

                this.applyPreviousResults(response.mentions);
                return this.resolveOutletsByHost(filteredMentions).pipe(
                    map(outletsByHost => ({ filteredMentions, outletsByHost }))
                );
            }),
            finalize(() => this.outletListLoading = false)
        ).subscribe({
            next: ({ filteredMentions, outletsByHost }) => this.applyResults(filteredMentions, outletsByHost),
            error: () => {
                this.answerEngineOptions.forEach(e => {
                    e.answerText = '';
                    e.sources = [];
                    e.loading = false;
                });
                this.outletList = [];
            }
        });
    }


    private applyResults(mentions: MentionModel[], outletsByHost: Map<string, OutletModel>) {
        // preserves insertion order
        const outletInfoById = new Map<string, ViSourceOutlet>();

        mentions.forEach(({ viMetadata, body }) => {
            const type = viApiToEngineType[viMetadata.answerEngine];
            const engine = this.answerEngineOptions.find(e => e.type === type);

            if (!engine) {
                return;
            }

            // An engine can cite the same domain twice, so dedupe per engine
            const uniqueHosts = [...new Set(
                (viMetadata.citedSources ?? [])
                    .map(source => this.getLookupHost(source))
                    .filter((host): host is string => !!host)
            )];

            const sources: ViAnswerEngineSource[] = uniqueHosts.map(host => {
                const outlet = outletsByHost.get(host) ?? null;

                if (outlet) {
                    const existing = outletInfoById.get(outlet.id);
                    if (existing) {
                        if (!existing.answerEngines.includes(type)) {
                            existing.answerEngines.push(type);
                        }
                    } else {
                        outletInfoById.set(outlet.id, {
                            outlet,
                            circulation: outlet.circulations?.find(ci => ci.isSimilarWeb),
                            answerEngines: [type]
                        });
                    }
                }

                return {
                    link: `https://${host}`,
                    outletId: outlet?.id,
                    outlet
                } as ViAnswerEngineSource;
            });

            engine.answerText = body;
            engine.sources = engine.answerText ? sources : [];
            engine.rawResponse = {
                ...viMetadata,
                text: engine.answerText,
                citations: viMetadata.citedSources.map(url => ({
                    author: '',
                    icon: '',
                    title: '',
                    url: url
                })),
                answerEngine: this.engineLabelMap[viMetadata.answerEngine]
            };

            engine.loading = false;
        });

        // Engines missing from the response
        this.answerEngineOptions
            .filter(e => e.loading)
            .forEach(e => {
                e.answerText = '';
                e.sources = [];
                e.loading = false;
            });

        this.outletList = [...outletInfoById.values()];

        if (!this.selectedEngineType) {
            this.selectedEngineType = this.answerEngineOptions.find(e => e.answerText)?.type;
        }
    }

    private getPreviousDay(selected: Date): Date | undefined {
        if (!selected) {
            return undefined;
        }

        const selectedKey = DateHelper.toDayKey(selected);

        // Options are newest-first, so the first earlier day is the closest one.
        const previousDayKey = this.dayOptions.find(key => key < selectedKey);

        return previousDayKey ? DateHelper.fromDayKey(previousDayKey) : undefined;
    }

    private applyPreviousResults(mentions: MentionModel[]) {

        const previousEngineOptions: AnswerEngineOption[] = this.getEngineOptions(this.currentMonitor.answerEngines ?? [])
            .map(engine => ({ ...engine, rawResponse: undefined }));

        if (this.previousDate) {
            mentions
                .filter(mention => {
                    return moment.utc(mention.publishedDate as number).format('YYYY-MM-DD') ===
                        DateHelper.toDayKey(this.previousDate);
                })
                .forEach(({ viMetadata }) => {
                    if (!viMetadata?.answerEngine) {
                        return;
                    }
                    const type = viApiToEngineType[viMetadata.answerEngine];
                    const engine = previousEngineOptions.find(e => e.type === type);
                    if (engine) {
                        engine.rawResponse = {
                            ...viMetadata,
                            text: engine.answerText,
                            citations: viMetadata.citedSources.map(url => ({
                                author: '',
                                icon: '',
                                title: '',
                                url: url
                            })),
                            answerEngine: this.engineLabelMap[viMetadata.answerEngine]
                        };
                    }
                });
        }

        this.previousAnswerEngineOptions = previousEngineOptions;
    }


    /**
     * Collect a map of all url sources, fetching the OutletModel only once.
     * @Returns host -> OutletModel map
     */
    private resolveOutletsByHost(mentions: MentionModel[]): Observable<Map<string, OutletModel>> {

        const hosts = new Set<string>(mentions
            .flatMap(mention => mention.viMetadata.citedSources ?? [])
            .map(url => this.getLookupHost(url))
            .filter((host): host is string => !!host && !isBlockedHost(host))
        );

        if (!hosts.size) {
            return of(new Map());
        }

        const hostList = [...hosts];

        return forkJoin(hostList.map(host => this.findOutletIdByHost(host))).pipe(
            switchMap(outletIds => {
                const hostToId = new Map<string, string>();
                hostList.forEach((host, i) => {
                    if (outletIds[i]) {
                        hostToId.set(host, outletIds[i]);
                    }
                });

                const uniqueIds = [...new Set(hostToId.values())];
                if (!uniqueIds.length) {
                    return of(new Map<string, OutletModel>());
                }

                return forkJoin(uniqueIds.map(id =>
                    this.outletsPublicApi.getOutlet(id).pipe(
                        map(outlet => outlet ? new OutletModel(outlet) : null),
                        catchError(() => of(null))
                    )
                )).pipe(
                    map(outlets => {
                        const idToOutlet = new Map<string, OutletModel>();
                        uniqueIds.forEach((id, i) => {
                            if (outlets[i]) {
                                idToOutlet.set(id, outlets[i]);
                            }
                        });

                        const hostToOutlet = new Map<string, OutletModel>();
                        hostToId.forEach((id, host) => {
                            const outlet = idToOutlet.get(id);
                            if (outlet) {
                                hostToOutlet.set(host, outlet);
                            }
                        });
                        return hostToOutlet;
                    })
                );
            })
        );
    }

    private findOutletIdByHost(host: string): Observable<string | undefined> {
        return this.journodexSearchesApi.searchContactsAndOutlets({
            type: SearchContactsAndOutletsTypeApiEnum.outlet,
            url: host
        }).pipe(
            map(outlets => outlets[0]?.uuid),
            catchError(() => of(undefined)) // one failed lookup shouldn't kill the forkJoin
        );
    }

    /** Accepts full URLs or bare domains. Null for empty or unparseable values. */
    private getLookupHost(source?: string): string | null {
        const value = source?.trim();
        if (!value) {
            return null;
        }
        try {
            const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
            return new URL(withScheme).host.toLowerCase();
        } catch {
            return null;
        }
    }

    private getDimensionCards<T extends ViQuestionApi>(questions: T[]): ViDimensionCard<T>[] {
        const grouped = groupBy(questions, x => x.dimension);

        return Object.values(ViDimensionEnum)
            .map(dimension => ({
                dimension,
                questions: grouped[dimension] ?? [],
                isExpanded: true,
            }))
            .filter(x => x.questions?.length > 0);
    }

    private getEngineOptions(engineTypes: AnswerEngineTypesEnum[]): AnswerEngineOption[] {
        if (!engineTypes) {

            return [];
        }

        const engineOptions: AnswerEngineOption[] = getAllEngineOptions();

        return engineOptions.filter(x => engineTypes.includes(x.type));
    }

    ngOnDestroy() {
        this.searchDestroy$.complete();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
